import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getBigBlueButtonActivities,
  getBigBlueButtonMeetingInfo,
  checkBigBlueButtonCanJoin,
  findCourseModuleIdForBBB,
  updateBBBcompletion
} from '../api/moodleBigBlueButtonApi';
import {
  BigBlueButtonActivity,
  LiveClassStatus
} from '../../types/bigBlueButton.types';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../api/instanceApi';

/**
 * Hook for managing BigBlueButton activities in a course
 */
export const useBigBlueButtonActivities = (courseId: number) => {
  return useQuery({
    queryKey: ['bigBlueButtonActivities', courseId],
    queryFn: () => getBigBlueButtonActivities(courseId),
    enabled: !!courseId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for managing BigBlueButton meeting info
 */
export const useBigBlueButtonMeetingInfo = (bigbluebuttonbnId: number) => {
  return useQuery({
    queryKey: ['bigBlueButtonMeetingInfo', bigbluebuttonbnId],
    queryFn: () => getBigBlueButtonMeetingInfo(bigbluebuttonbnId),
    enabled: !!bigbluebuttonbnId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: false, // Disable automatic refetch
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for getting join URL
 */
export const useHandleViewBigBlueButton = () => {
  const viewBigBlueButtonMutation = useMutation({
    mutationFn: (bigbluebuttonbnId: number) => updateBBBcompletion(bigbluebuttonbnId),
    onError: handleApiError,
  });
  return {
    viewBigBlueButton: viewBigBlueButtonMutation.mutate,
    viewBigBlueButtonAsync: viewBigBlueButtonMutation.mutateAsync,
  };
};

/**
 * Hook for comprehensive live class management
 */
export const useLiveClass = (bigbluebuttonbnId: number, activity?: BigBlueButtonActivity) => {
  const { t } = useTranslation();
  const [liveClassStatus, setLiveClassStatus] = useState<LiveClassStatus>({
    isScheduled: false,
    isRunning: false,
    isWaitingForModerator: false,
    isEnded: false,
    canJoin: false,
  });

  const [joinError, setJoinError] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [courseModuleId, setCourseModuleId] = useState<number | null>(null);

  // Get course module ID first
  useEffect(() => {
    if (bigbluebuttonbnId && !courseModuleId) {
      findCourseModuleIdForBBB(bigbluebuttonbnId).then(cmid => {
        if (cmid) {
          setCourseModuleId(cmid);
        }
      });
    }
  }, [bigbluebuttonbnId, courseModuleId]);

  // Use the new can_join API
  const { data: canJoinResult, isLoading: isCanJoinLoading } = useQuery({
    queryKey: ['bigBlueButtonCanJoin', courseModuleId],
    queryFn: () => courseModuleId ? checkBigBlueButtonCanJoin(courseModuleId) : null,
    enabled: !!courseModuleId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: false, // Disable automatic refetch
    refetchOnWindowFocus: false,
  });

  const { data: meetingInfo, isLoading: isMeetingInfoLoading } = useBigBlueButtonMeetingInfo(bigbluebuttonbnId);

  // Simplified - remove unused hooks
  const recordings: any[] = [];
  const isRecordingsLoading = false;

  // Calculate live class status using new can_join API
  useEffect(() => {
    if (!activity || isCanJoinLoading) return;

    // Use can_join API result as primary source
    if (canJoinResult?.success && canJoinResult.data) {
      const { canJoin, message } = canJoinResult.data;

      let status = 'unknown';
      let isRunning = false;
      let isWaitingForModerator = false;

      // Analyze message to determine specific status
      if (message) {
        const lowerMessage = message.toLowerCase();

        if (canJoin) {
          if (lowerMessage.includes('đang diễn ra') || lowerMessage.includes('running')) {
            status = 'running';
            isRunning = true;
          } else {
            status = 'available';
          }
        } else {
          if (lowerMessage.includes('chờ') || lowerMessage.includes('waiting')) {
            status = 'waiting_for_moderator';
            isWaitingForModerator = true;
          } else if (lowerMessage.includes('kết thúc') || lowerMessage.includes('ended')) {
            status = 'ended';
          } else if (lowerMessage.includes('chưa bắt đầu') || lowerMessage.includes('not started')) {
            status = 'not_started';
          } else {
            status = 'not_available';
          }
        }
      }

      const now = Date.now() / 1000;
      const openingTime = activity.openingtime;
      const closingTime = activity.closingtime;

      const newStatus: LiveClassStatus = {
        isScheduled: openingTime > 0 || closingTime > 0,
        isRunning: isRunning,
        isWaitingForModerator: isWaitingForModerator,
        isEnded: status === 'ended' || (closingTime > 0 && now > closingTime),
        canJoin: canJoin,
        startTime: openingTime > 0 ? openingTime : undefined,
        endTime: closingTime > 0 ? closingTime : undefined,
        participantCount: 0, // Will be updated from meeting info if available
        maxParticipants: activity.userlimit > 0 ? activity.userlimit : undefined,
        statusMessage: message || (canJoin ? t('liveClasses.status.canJoin') : t('liveClasses.status.cannotJoin')),
      };

      // Status calculated from can_join API
      setLiveClassStatus(newStatus);
      return;
    }

    // Fallback logic when can_join API is not available

    const now = Date.now() / 1000;
    const openingTime = activity.openingtime;
    const closingTime = activity.closingtime;

    let canJoin = false;
    let statusMessage = '';

    // Basic time-based logic as fallback
    if (closingTime > 0 && now > closingTime) {
      canJoin = false;
      statusMessage = t('liveClasses.status.ended');
    } else if (openingTime > 0 && now < openingTime) {
      canJoin = false;
      const timeUntilStart = openingTime - now;
      const hours = Math.floor(timeUntilStart / 3600);
      const minutes = Math.floor((timeUntilStart % 3600) / 60);
      statusMessage = t('liveClasses.status.startingSoon', { hours, minutes });
    } else {
      // In time window - default to allowing join but show warning
      canJoin = true;
      statusMessage = t('liveClasses.status.checkStatus');
    }

    const fallbackStatus: LiveClassStatus = {
      isScheduled: openingTime > 0 || closingTime > 0,
      isRunning: false, // Can't determine without API
      isWaitingForModerator: false,
      isEnded: closingTime > 0 && now > closingTime,
      canJoin: canJoin,
      startTime: openingTime > 0 ? openingTime : undefined,
      endTime: closingTime > 0 ? closingTime : undefined,
      participantCount: 0,
      maxParticipants: activity.userlimit > 0 ? activity.userlimit : undefined,
      statusMessage: statusMessage,
    };

    // Fallback status calculated
    setLiveClassStatus(fallbackStatus);
  }, [activity, canJoinResult, isCanJoinLoading, courseModuleId, bigbluebuttonbnId, t]);

  // Update participant count from meeting info if available
  useEffect(() => {
    if (meetingInfo && !isMeetingInfoLoading) {
      const meetingData = meetingInfo;

      // Get participant count from meeting info if available
      const participantCount = meetingData?.info?.participantCount
        ? parseInt(meetingData.info.participantCount, 10)
        : (meetingData?.participantCount || 0);

      if (participantCount > 0) {
        setLiveClassStatus(prev => ({
          ...prev,
          participantCount: participantCount
        }));
      }

      // Participant count updated
    }
  }, [meetingInfo, isMeetingInfoLoading]);

  const joinMeeting = useCallback(async () => {
    if (isJoining) {
      return;
    }

    setIsJoining(true);
    setJoinError(null);

    try {
      // Only allow join if meeting is running
      if (!liveClassStatus.isRunning) {
        throw new Error(t('liveClasses.status.notStarted'));
      }

    } catch (error) {
      // Error joining meeting
      const errorMessage = error instanceof Error ? error.message : t('liveClasses.status.cannotJoin');
      setJoinError(errorMessage);
      throw error;
    } finally {
      setIsJoining(false);
    }
  }, [liveClassStatus.isRunning, isJoining, t]);

  return {
    liveClassStatus,
    meetingInfo,
    recordings,
    joinMeeting,

    isLoading: isMeetingInfoLoading,
    isRecordingsLoading,
    isJoining,
    joinError,
  };
};
